import { useId } from "react";
import type { ReactNode } from "react";
import { cn } from "@/lib/cn";

export interface TooltipProps {
  label: string;
  children: ReactNode;
  side?: "top" | "right" | "bottom";
  className?: string;
}

/**
 * Keyboard-accessible tooltip: shown on hover/focus via CSS (no `title` attr,
 * which is not reliably keyboard-reachable or announced consistently).
 */
export function Tooltip({ label, children, side = "top", className }: TooltipProps) {
  const id = useId();
  const position =
    side === "right"
      ? "left-full top-1/2 ml-2 -translate-y-1/2"
      : side === "bottom"
        ? "top-full left-1/2 mt-2 -translate-x-1/2"
        : "bottom-full left-1/2 mb-2 -translate-x-1/2";

  return (
    <span className={cn("group relative inline-flex", className)}>
      <span aria-describedby={id}>{children}</span>
      <span
        id={id}
        role="tooltip"
        className={cn(
          "pointer-events-none absolute z-50 rounded-xs border border-border bg-surface-3 px-2 py-1 text-small whitespace-nowrap text-text-primary opacity-0 shadow-[var(--shadow-popover)] transition-opacity duration-100 group-hover:opacity-100 group-focus-within:opacity-100",
          position,
        )}
      >
        {label}
      </span>
    </span>
  );
}
